import React, { useEffect } from 'react';
import { useStore } from 'effector-react';
import { formatSize, toFlag } from 'pano-utils';
import { type HSIconVideo, ImageType, LibType } from '@types';
import addComponent from '@utils/addComponent';
import { Validator } from '@components/Validator';
import ImageContainer from '@components/ImageContainer';
import Image from '@components/Image';
import Button from '@components/Button';
import TextLink from '@components/TextLink';
import Tip from '@components/Tip';
import useToggle from '@hooks/useToggle';
import getVideoDetail from '@utils/getVideoDetail';
import VideoCaptureModal from '@components/VideoCaptureModal';
import uploadVideoCover from '@utils/uploadVideoCover';
import { getMediaUrl } from '@utils/urlMakers';
import {
  $editingHotspot,
  $editingHotspotApi,
} from '@stores/editingHotspotStores';
import { MainPlayer } from '@stores/krpPlayerStores';
import FeatureToggle from '@components/FeatureToggle';
import { $playerWidgetsApi } from '@stores/playerWidgets';
import { reAddEditingHotspot } from '../../../../hotspot';
import { LIB_SIZE_LIMIT, selectLibVideos } from '../../../../mediaLib';
import StickSetting from '../StickSetting';

const size = formatSize(LIB_SIZE_LIMIT[LibType.video]);

function IconSettingVideo() {
  const { hotspot } = useStore($editingHotspot)!;
  const icon = hotspot.icon as HSIconVideo;
  const media = icon.media[0];
  const loading = useToggle(false);

  useEffect(() => {
    return () => {
      $playerWidgetsApi.remove('icon');
    };
  }, []);

  useEffect(() => {
    if (media) {
      $playerWidgetsApi.add('icon');
    } else {
      $playerWidgetsApi.remove('icon');
    }
  }, [!media]);

  const handleSelectVideo = async () => {
    const { libItems } = await selectLibVideos();
    if (!libItems) return;
    loading.on();
    const item = libItems[0];
    const { coverUrl } = await getVideoDetail(item.url);
    $editingHotspotApi.update((draft) => {
      (draft.icon as HSIconVideo).media = [
        { id: item!.id, url: item!.url, coverUrl, name: item!.name },
      ];
    });
    loading.off();
    reAddEditingHotspot();
  };

  const handleCoverSetting = () => {
    addComponent((remove) => (
      <VideoCaptureModal
        coverSrc={media!.coverUrl}
        videoUrl={getMediaUrl(media!.url)}
        title="视频封面图"
        onRequestClose={remove}
        onConfirm={async (file) => {
          const url = await uploadVideoCover(file);
          $editingHotspotApi.update((draft) => {
            (draft.icon as HSIconVideo).media[0]!.coverUrl = url;
          });
          MainPlayer.get()?.updateVideoHotspot({
            id: hotspot.id,
            posterUrl: getMediaUrl(url),
          });
        }}
      />
    ));
  };

  const handleVideoDelete = () => {
    $editingHotspotApi.update((draft) => {
      (draft.icon as HSIconVideo).media = [];
    });
    reAddEditingHotspot();
  };

  const handleFlagChange =
    (
      key:
        | 'autoPlay'
        | 'loop'
        | 'mute'
        | 'turnOffBgMusicOnPlay'
        | 'turnOffBgVoiceOnPlay'
    ) =>
    (bool: boolean) => {
      $editingHotspotApi.update((draft) => {
        (draft.icon as HSIconVideo)[key] = toFlag(bool);
      });
    };

  return (
    <>
      <Validator
        value={media}
        validFn={(value) => !!value}
        warningMessage="请选择视频"
      >
        {({ warning, value }) => (
          <ImageContainer
            warning={warning}
            className="h-70"
            placeholder={
              <div className="flex flex-col items-center">
                {loading.isOn ? (
                  <div className="text-white">视频封面正在生成中…</div>
                ) : (
                  <>
                    <p>视频格式：MP4</p>
                    <p>编码方式：H.264</p>
                  </>
                )}
              </div>
            }
          >
            {!!value && (
              <>
                <Image
                  className="h-50 w-50"
                  size={50}
                  src={value.coverUrl}
                  type={ImageType.media}
                />
                <div className="ml-10 flex flex-col items-start">
                  <div className="mb-5 w-140 truncate text-white">
                    {value.name}
                  </div>
                  <div className="flex items-center">
                    <TextLink onClick={handleCoverSetting}>设置封面</TextLink>
                    <TextLink className="ml-15" onClick={handleVideoDelete}>
                      删除
                    </TextLink>
                  </div>
                </div>
              </>
            )}
          </ImageContainer>
        )}
      </Validator>
      <div className="mt-10 flex items-center justify-between">
        <Tip>{`文件体积不超过${size}`}</Tip>
        <Button size="sm" onClick={handleSelectVideo}>
          选择视频
        </Button>
      </div>
      {!!media && (
        <>
          <FeatureToggle
            className="mt-20"
            title={
              <div className="flex items-center">
                <span>自动播放</span>
                <Tip className="ml-5">（仅PC端有效）</Tip>
              </div>
            }
            isOn={!!icon.autoPlay}
            onToggle={handleFlagChange('autoPlay')}
          />
          <FeatureToggle
            className="mt-20"
            title="循环播放"
            isOn={!!icon.loop}
            onToggle={handleFlagChange('loop')}
          />
          <FeatureToggle
            className="mt-20"
            title="静音"
            isOn={!!icon.mute}
            onToggle={handleFlagChange('mute')}
          />
          <StickSetting />
        </>
      )}
    </>
  );
}

export default IconSettingVideo;
